// ===============================================================
// File: src/contexts/StudioSpaceContext.jsx
// Description: Studio Space booking system - connects Studio World + Visitor World
// Features:
// - Studio configures spaces (equipment, capacity, pricing)
// - Visitors book spaces (date/time, seats, payment)
// - Real-time availability tracking
// - Liquid glass UI styling integration
// ===============================================================

import React, { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import { supabase, hasSupabase } from "../supabaseClient";
import { useActiveStudioId } from "../hooks/useActiveStudioId";
import { useNotifications } from "./NotificationsContext";
import { getStudioLogoUrl } from "../lib/studioLogo";
import { useStorage } from "./StorageContext";

/**
 * DATA MODEL
 * 
 * StudioSpace = {
 *   id, name, location, description,
 *   resources: { potteryWheels, handBuildingTables, kilns, glazeBooth, shelves },
 *   tags: string[],
 *   capacity: { seats, kilnSlots },
 *   pricing: { perHour, perDay, currency },
 *   availability: { statusBadge },
 *   heroImageUrl, accentColor
 * }
 * 
 * StudioSpaceBooking = {
 *   id, spaceId, date, startTime, endTime, seats,
 *   bookedBy: { name, email },
 *   notes, paymentStatus, createdAt
 * }
 */

function nowIso() {
  return new Date().toISOString();
}

function readLS(key, fallback) {
  try {
    return JSON.parse(localStorage.getItem(key) || JSON.stringify(fallback));
  } catch {
    return fallback;
  }
}

function writeLS(key, val) {
  try {
    localStorage.setItem(key, JSON.stringify(val));
    // Keep legacy event for any listeners; avoid custom spaces event to prevent loops
    window.dispatchEvent(new Event("mudo:studio-bookings-change"));
  } catch {}
}

function resolveSeatCount(space) {
  const raw =
    space?.seats_total ??
    space?.seatsTotal ??
    space?.capacity?.seats ??
    space?.capacity_seats ??
    space?.seats ??
    1;
  const num = Number(raw);
  return Number.isFinite(num) && num > 0 ? Math.floor(num) : 1;
}

function normalizeSpace(space) {
  if (!space) return space;
  const safeSeats = resolveSeatCount(space);
  const normalizedCapacity = {
    ...(space.capacity || {}),
    seats: space.capacity?.seats ?? safeSeats,
  };

  return {
    ...space,
    seats_total: space.seats_total ?? safeSeats,
    seatsTotal: space.seatsTotal ?? safeSeats,
    capacity_seats: space.capacity_seats ?? safeSeats,
    capacity: normalizedCapacity,
    pricing: space.pricing ?? space.pricing_json ?? space.prices ?? null,
    availability: space.availability ?? space.availability_json ?? null,
    active: space.active ?? space.is_public ?? (space.is_active ?? true),
  };
}

function normalizeList(spaces = []) {
  return spaces.map(normalizeSpace);
}

// Enhanced default spaces with visitor-facing data
const DEFAULT_SPACES = [
  {
    id: "pottery-studio-1",
    name: "Main Pottery Studio",
    location: "Studio A - Ground Floor",
    description: "Spacious pottery studio with 6 wheels, hand-building tables, and full kiln access.",
    resources: {
      potteryWheels: true,
      handBuildingTables: true,
      kilns: true,
      glazeBooth: true,
      shelves: true,
    },
    tags: ["Pottery Wheels", "Kilns", "Hand Building", "Glaze Station"],
    capacity: { seats: 8, kilnSlots: 2 },
    pricing: {
      currency: "AUD",
      hourly: 25,
      daily: 180,
      weekly: 1100,
      monthly: 4000,
      perKg: null,
    },
    availability: { statusBadge: "Available today" },
    heroImageUrl: null,
    accentColor: "#f97316",
  },
  {
    id: "hand-building-room",
    name: "Hand Building Room",
    location: "Studio B - Upper Level",
    description: "Quiet space perfect for hand-building and sculptural work with ample table space.",
    resources: {
      potteryWheels: false,
      handBuildingTables: true,
      kilns: false,
      glazeBooth: false,
      shelves: true,
    },
    tags: ["Hand Building", "Sculpture", "Clay Storage"],
    capacity: { seats: 6 },
    pricing: {
      currency: "AUD",
      hourly: 20,
      daily: 140,
      weekly: null,
      monthly: null,
      perKg: null,
    },
    availability: { statusBadge: "Available from tomorrow" },
    heroImageUrl: null,
    accentColor: "#10b981",
  },
];

const StudioSpaceCtx = createContext(null);

export function StudioSpaceProvider({ children }) {
  // Resolve active studio id (must be a valid UUID referencing a row in studios table)
  const { studioId } = useActiveStudioId();
  const { loadJSON, saveJSON, hasFolder, isDriveLinked, isDriveAuthed } = useStorage() || {};
  const hasWorkspace = !!hasFolder || (!!isDriveLinked && !!isDriveAuthed);
  const spacesKey = studioId ? `mudo:studioSpaces:${studioId}` : "mudo:studioSpaces";
  const bookingsKey = studioId ? `mudo:studioBookings:${studioId}` : "mudo:studioBookings";
  const SPACES_PATH = studioId ? `studio/${studioId}/spaces.json` : "studio/spaces.json";
  const BOOKINGS_PATH = studioId ? `studio/${studioId}/bookings.json` : "studio/bookings.json";
  const lastSpacesKey = useRef(spacesKey);
  const lastBookingsKey = useRef(bookingsKey);
  const [spaces, setSpaces] = useState(() => normalizeList(readLS(spacesKey, DEFAULT_SPACES)));
  const [bookings, setBookings] = useState(() => readLS(bookingsKey, []));
  const [loadedFromSupabase, setLoadedFromSupabase] = useState(false);
  const { addNotification } = useNotifications() || {};

  // Rehydrate when switching studios to avoid leaking cached data across tenants
  useEffect(() => {
    setSpaces(normalizeList(readLS(spacesKey, DEFAULT_SPACES)));
    setBookings(readLS(bookingsKey, []));
  }, [spacesKey, bookingsKey]);

  // Hydrate from Drive/Folder when workspace available
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!hasWorkspace || !loadJSON) return;
      try {
        const s = await loadJSON(SPACES_PATH, null);
        if (alive && s) setSpaces(normalizeList(Array.isArray(s) ? s : DEFAULT_SPACES));
      } catch {}
      try {
        const b = await loadJSON(BOOKINGS_PATH, []);
        if (alive && Array.isArray(b)) setBookings(b);
      } catch {}
    })();
    return () => { alive = false; };
  }, [hasWorkspace, loadJSON, SPACES_PATH, BOOKINGS_PATH]);

  useEffect(() => {
    if (lastSpacesKey.current !== spacesKey) {
      lastSpacesKey.current = spacesKey;
      return;
    }
    writeLS(spacesKey, spaces);
  }, [spacesKey, spaces]);

  // Persist spaces to Drive/Folder (debounced)
  useEffect(() => {
    if (!hasWorkspace || !saveJSON) return;
    const t = setTimeout(async () => {
      try { await saveJSON(SPACES_PATH, spaces); } catch {}
    }, 500);
    return () => clearTimeout(t);
  }, [spaces, SPACES_PATH, hasWorkspace, saveJSON]);

  // Multi-tab sync: native storage event (only fires in OTHER tabs, not current)
  useEffect(() => {
    const onStorageChange = (e) => {
      if (e.key === spacesKey && e.newValue) {
        try {
          const updated = JSON.parse(e.newValue);
          setSpaces(normalizeList(updated));
        } catch {}
      }
      if (e.key === bookingsKey && e.newValue) {
        try {
          const updated = JSON.parse(e.newValue);
          setBookings(updated);
        } catch {}
      }
    };
    window.addEventListener('storage', onStorageChange);
    return () => window.removeEventListener('storage', onStorageChange);
  }, [spacesKey, bookingsKey]);
  useEffect(() => {
    if (lastBookingsKey.current !== bookingsKey) {
      lastBookingsKey.current = bookingsKey;
      return;
    }
    writeLS(bookingsKey, bookings);
  }, [bookingsKey, bookings]);

  // Persist bookings to Drive/Folder (debounced)
  useEffect(() => {
    if (!hasWorkspace || !saveJSON) return;
    const t = setTimeout(async () => {
      try { await saveJSON(BOOKINGS_PATH, bookings); } catch {}
    }, 500);
    return () => clearTimeout(t);
  }, [bookings, BOOKINGS_PATH, hasWorkspace, saveJSON]);

  // Load from Supabase if configured
  useEffect(() => {
    if (!hasSupabase()) return;
    if (!studioId) return;
    let isMounted = true;

    async function loadAll() {
      // studio_bookings doesn't have studio_id, so join through space_id -> studio_spaces.id -> studio_spaces.studio_id
      const bookingsQuery = supabase
        .from("studio_bookings")
        .select("*, space:studio_spaces!inner(studio_id)")
        .eq("space.studio_id", studioId);

      const [bookingsRes, studiosRes] = await Promise.all([
        bookingsQuery,
        supabase.from("studios").select("id, name, settings, stripe_account_id_resolved"),
      ]);
      if (!isMounted) return;
      
      // Build studio map for quick lookup
      const studioMap = {};
      if (!studiosRes.error && Array.isArray(studiosRes.data)) {
        studiosRes.data.forEach((s) => {
          const resolvedLogo = getStudioLogoUrl(s);
          studioMap[s.id] = {
            id: s.id,
            name: s.name,
            settings: s.settings ?? null,
            image_url: resolvedLogo,
            logo_url: resolvedLogo,
            stripe_account_id_resolved: s.stripe_account_id_resolved || null,
          };
        });
      }
      
      if (!bookingsRes.error && Array.isArray(bookingsRes.data)) {
        const mappedBookings = bookingsRes.data.map((r) => ({
          id: r.id,
          spaceId: r.space_id,
          date: r.date,
          startTime: r.start_time ? String(r.start_time).slice(0,5) : null,
          endTime: r.end_time ? String(r.end_time).slice(0,5) : null,
          seats: r.seats || 1,
          bookedBy: r.booked_by || null,
          notes: r.notes || "",
          paymentStatus: r.payment_status || "pending",
          createdAt: r.created_at,
        }));
        setBookings(mappedBookings);
      }
      setLoadedFromSupabase(true);
    }

    loadAll();

    // realtime subscriptions with studio_id filter to prevent cross-studio updates
    const channelName = `studio-spaces-${studioId}`;
    const channel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        { 
          event: "*", 
          schema: "public", 
          table: "studio_bookings"
        },
        (payload) => {
          // Re-fetch bookings on any change (filter by studio_id via join)
          const bookingsQuery = supabase
            .from("studio_bookings")
            .select("*, space:studio_spaces!inner(studio_id)")
            .eq("space.studio_id", studioId);
          
          bookingsQuery
            .then(({ data, error }) => {
              if (error || !data) return;
              const mapped = data.map((r) => ({
                id: r.id,
                spaceId: r.space_id,
                date: r.date,
                startTime: r.start_time ? String(r.start_time).slice(0,5) : null,
                endTime: r.end_time ? String(r.end_time).slice(0,5) : null,
                seats: r.seats || 1,
                bookedBy: r.booked_by || null,
                notes: r.notes || "",
                paymentStatus: r.payment_status || "pending",
                createdAt: r.created_at,
              }));
              setBookings(mapped);
            });
        }
      )
      .subscribe();

    return () => {
      isMounted = false;
      try { supabase.removeChannel(channel); } catch {}
    };
  }, [studioId]);

  // === STUDIO WORLD HELPERS ===
  
  const createOrUpdateSpace = useCallback(async (space) => {
    const id = space.id || crypto.randomUUID();
    const normalizedNext = normalizeSpace({ ...space, id });
    setSpaces(prev => {
      const existing = prev.find(s => s.id === id);
      if (existing) return prev.map(s => (s.id === id ? { ...s, ...normalizedNext } : s));
      return [...prev, normalizedNext];
    });
    return normalizedNext;
  }, [studioId]);

  const deleteSpace = useCallback(async (id) => {
    setSpaces(prev => prev.filter(s => s.id !== id));
    setBookings(prev => prev.filter(b => b.spaceId !== id));
    if (hasSupabase()) {
      try {
        await supabase.from("studio_spaces").delete().eq("id", id);
      } catch (err) {
        console.warn('[StudioSpaceContext] Delete space failed', err);
      }
    }
  }, []);

  // === VISITOR WORLD HELPERS ===
  
  const createBooking = useCallback(async (payload) => {
    const { spaceId, date, startTime, endTime, seats, bookedBy, notes, durationType, quantity } = payload;
    
    // Find space
    const space = spaces.find(s => s.id === spaceId);
    if (!space) throw new Error("Space not found");
    
    // Helper: detect kiln by explicit type or tag
    const isKiln = (space?.type === "kiln") || (space?.tags || []).includes("kiln");

    // Optional: validate times against configured schedule
    const schedule = space?.availability?.schedule || null;
    const timeToNum = (t) => {
      if (!t) return null; const [h,m] = String(t).split(":").map(Number); return h*60 + (m||0);
    };
    const withinAnyWindow = () => {
      if (!schedule) return true;
      const day = new Date(date);
      const dowIdx = (day.getDay() + 6) % 7; // Mon=0
      const dowMap = ["mon","tue","wed","thu","fri","sat","sun"];
      const dow = dowMap[dowIdx];
      const startN = timeToNum(startTime);
      const endN = timeToNum(endTime);
      if (startN == null || endN == null) return false;
      const openN = timeToNum(schedule.open);
      const closeN = timeToNum(schedule.close);
      if (schedule.type === "daily") {
        return startN >= openN && endN <= closeN;
      }
      if (schedule.type === "weekly") {
        const days = schedule.days || [];
        if (!days.includes(dow)) return false;
        return startN >= openN && endN <= closeN;
      }
      if (schedule.type === "monthly") {
        const dnum = day.getDate();
        const days = (schedule.days || []).map(Number);
        if (!days.includes(dnum)) return false;
        return startN >= openN && endN <= closeN;
      }
      return true;
    };
    if (!withinAnyWindow()) {
      throw new Error("Selected time is outside this space's schedule");
    }

    // Determine booking units
    const requestedSeats = (() => {
      if (isKiln && durationType === "perKg") {
        // reuse seats column to represent kilograms
        const q = Number(quantity || 0);
        return Number.isFinite(q) && q > 0 ? Math.floor(q) : 0;
      }
      const s = Number(seats || 1);
      return Number.isFinite(s) && s > 0 ? s : 1;
    })();

    // Check capacity upper bound (static)
    if (isKiln && durationType === "perKg") {
      const kgMax = Number(space.capacity?.kgMax || 0);
      if (!kgMax || requestedSeats > kgMax) {
        throw new Error(kgMax ? `Cannot book more than ${kgMax} kg for this kiln` : "Kiln capacity (kg) not configured");
      }
    } else {
      if (requestedSeats > (space.capacity?.seats || 0)) {
        throw new Error(`Maximum ${space.capacity.seats} seats available`);
      }
    }
    
    // Check for conflicts
    const conflicts = bookings.filter(b => {
      if (b.spaceId !== spaceId || b.date !== date) return false;
      // Simple time overlap check
      if (!startTime || !endTime || !b.startTime || !b.endTime) return false;
      const bStart = b.startTime.replace(":", "");
      const bEnd = b.endTime.replace(":", "");
      const rStart = startTime.replace(":", "");
      const rEnd = endTime.replace(":", "");
      return (rStart < bEnd && rEnd > bStart);
    });
    
    const bookedSeats = conflicts.reduce((sum, b) => sum + (b.seats || 0), 0);
    if (isKiln && durationType === "perKg") {
      const kgMax = Number(space.capacity?.kgMax || 0);
      if (!kgMax) throw new Error("Kiln capacity (kg) not configured");
      if (bookedSeats + requestedSeats > kgMax) {
        const remaining = Math.max(0, kgMax - bookedSeats);
        throw new Error(remaining > 0
          ? `Only ${remaining} kg remaining for this time slot`
          : "No kiln capacity remaining for this time slot");
      }
    } else {
      if (bookedSeats + requestedSeats > (space.capacity?.seats || 0)) {
        throw new Error("Not enough seats available for this time slot");
      }
    }
    
    const booking = {
      id: `bkg_${crypto.randomUUID()}`,
      spaceId,
      date,
      startTime,
      endTime,
      seats: requestedSeats,
      bookedBy,
      notes: notes || "",
      paymentStatus: "pending",
      createdAt: nowIso(),
    };
    setBookings(prev => [booking, ...prev]);
    if (addNotification) {
      addNotification({
        world: "studio",
        type: "studio-space-booking",
        title: `${bookedBy?.name || bookedBy?.email || "Visitor"} booked ${space.name}`,
        body: `${date} • ${startTime || ""}${startTime && endTime ? " → " : ""}${endTime || ""}`,
        target: { entity: "studio-space", entityId: spaceId, route: "/studio-space" },
        severity: "info",
        meta: { bookingId: booking.id, spaceId },
      });
      addNotification({
        world: "visitor",
        type: "studio-space-booking",
        title: `Booking confirmed: ${space.name}`,
        body: `${date} • ${startTime || ""}${startTime && endTime ? " → " : ""}${endTime || ""}`,
        target: { entity: "studio-space", route: "/discover/spaces" },
        severity: "success",
        meta: { bookingId: booking.id, spaceId },
      });
    }

    if (hasSupabase()) {
      const { data, error } = await supabase.from("studio_bookings").insert({
        id: booking.id,
        space_id: booking.spaceId,
        date: booking.date,
        start_time: booking.startTime,
        end_time: booking.endTime,
        seats: booking.seats,
        booked_by: booking.bookedBy || null,
        notes: booking.notes,
        payment_status: booking.paymentStatus,
      }).select().single();
      if (!error && data) {
        // Normalize return
        const mapped = {
          id: data.id,
          spaceId: data.space_id,
          date: data.date,
          startTime: data.start_time ? String(data.start_time).slice(0,5) : booking.startTime,
          endTime: data.end_time ? String(data.end_time).slice(0,5) : booking.endTime,
          seats: data.seats || booking.seats,
          bookedBy: data.booked_by || booking.bookedBy,
          notes: data.notes || booking.notes,
          paymentStatus: data.payment_status || booking.paymentStatus,
          createdAt: data.created_at || booking.createdAt,
        };
        setBookings(prev => prev.map(b => (b.id === booking.id ? mapped : b)));
        return mapped;
      }
    }
    return booking;
  }, [spaces, bookings]);

  const markBookingPaid = useCallback(async (bookingId) => {
    const booking = bookings.find((b) => b.id === bookingId);
    setBookings(prev => prev.map(b => (b.id === bookingId ? { ...b, paymentStatus: "paid" } : b)));
    if (hasSupabase()) {
      await supabase.from("studio_bookings").update({ payment_status: "paid" }).eq("id", bookingId);
    }
    if (addNotification && booking) {
      addNotification({
        world: "studio",
        type: "system-billing",
        title: "Space booking paid",
        body: `${booking.bookedBy?.name || "Booking"} is marked as paid.`,
        target: { entity: "billing", route: "/billing" },
        severity: "success",
        meta: { bookingId },
      });
    }
  }, [bookings, addNotification]);

  const cancelBooking = useCallback(async (bookingId) => {
    const booking = bookings.find((b) => b.id === bookingId);
    setBookings(prev => prev.filter(b => b.id !== bookingId));
    if (hasSupabase()) {
      await supabase.from("studio_bookings").delete().eq("id", bookingId);
    }
    if (addNotification && booking) {
      addNotification({
        world: "studio",
        type: "studio-space-message",
        title: `Booking cancelled: ${booking.bookedBy?.name || bookingId}`,
        body: `${booking.date} slot for ${booking.spaceId} was cancelled.`,
        target: { entity: "studio-space", entityId: booking.spaceId, route: "/studio-space" },
        severity: "warning",
        meta: { bookingId },
      });
      addNotification({
        world: "visitor",
        type: "studio-space-message",
        title: `Space booking cancelled`,
        body: `Your booking for ${booking.spaceId} on ${booking.date} was cancelled.`,
        target: { entity: "studio-space", route: "/discover/spaces" },
        severity: "warning",
        meta: { bookingId },
      });
    }
  }, [bookings, addNotification]);

  // === AVAILABILITY HELPERS ===
  
  const getAvailabilityForSpace = useCallback((spaceId, date) => {
    const space = spaces.find(s => s.id === spaceId);
    if (!space) return { available: false, totalSeats: 0, bookedSeats: 0 };
    
    const dayBookings = bookings.filter(b => b.spaceId === spaceId && b.date === date);
    const bookedSeats = dayBookings.reduce((sum, b) => sum + (b.seats || 0), 0);
    const totalSeats = space.capacity?.seats || 0;
    
    return {
      available: bookedSeats < totalSeats,
      totalSeats,
      bookedSeats,
      remainingSeats: totalSeats - bookedSeats,
      bookings: dayBookings,
    };
  }, [spaces, bookings]);

  const getSpaceById = useCallback((id) => spaces.find(s => s.id === id), [spaces]);

  const listBookingsForDay = useCallback((dateISO) => {
    const d = new Date(dateISO);
    const dateStr = d.toISOString().split('T')[0];
    return bookings
      .filter(b => b.date === dateStr)
      .sort((a, b) => (a.startTime || "").localeCompare(b.startTime || ""));
  }, [bookings]);

  const value = useMemo(() => ({
    // State
    spaces,
    bookings,
    
    // Studio World methods
    createOrUpdateSpace,
    deleteSpace,
    
    // Visitor World methods
    createBooking,
    markBookingPaid,
    cancelBooking,
    
    // Query helpers
    getSpaceById,
    getAvailabilityForSpace,
    listBookingsForDay,
  }), [
    spaces,
    bookings,
    createOrUpdateSpace,
    deleteSpace,
    createBooking,
    markBookingPaid,
    cancelBooking,
    getSpaceById,
    getAvailabilityForSpace,
    listBookingsForDay,
  ]);

  return (
    <StudioSpaceCtx.Provider value={value}>
      {children}
    </StudioSpaceCtx.Provider>
  );
}

export function useStudioSpaces() {
  const ctx = useContext(StudioSpaceCtx);
  if (!ctx) throw new Error("useStudioSpaces must be used within StudioSpaceProvider");
  return ctx;
}
